// Pass-through DB read/write routing + graft helper, extracted from
// DatabaseWrapper so they can be unit-tested without pulling in
// pouchdb-browser (which references `self` and fails to load in node).
// See design-docs/pass-through-db.md.
//
// This module used to be the shared home for every testable sync helper,
// whatever its subject, because they all needed to live outside
// database-wrapper.ts for the reason above. Each of those subjects now has its
// own database-wrapper-*.ts sibling (deferred finding df-08d4a072f17b), so what
// is left here is the pass-through routing this file is named after. A new
// testable sync helper belongs in the sibling that matches what it does, or in
// a new one — not here.

import { PouchDoc } from '@cf-shared/data-model/pouch';
import { localOpName, trackLocalOpAsync } from './local-op-tracker';
import { parseRevGeneration } from './sync-rev-and-time-utils';
import {
  withLocalStoreTimeout,
  isLocalStoreNotAnsweringError,
  LOCAL_STORE_BOOT_OP_TIMEOUT_MS,
  runningTimeTimer,
} from './local-store-timeout';
// type-only import — elided at runtime, so this file is still safe to load
// under node (where pouchdb-browser's top-level `self` reference would fail).
import type PouchDB from 'pouchdb-browser';

/** DB-instance handle alias — uses PouchDB's type but never instantiates
 *  it (the wrapper does that). The routing logic only compares handles
 *  for identity (`db === remoteDb`); the graft helper additionally uses
 *  `bulkDocs` on `localDb` via the PassThroughGraftDeps subset below. */
export type DbHandle = PouchDB;

/** Shape that the chooseReadDb/chooseWriteDb routers need. Matches the
 *  relevant fields on DatabaseWrapper. */
export interface PassThroughDbState {
  remoteDb: DbHandle | null;
  localDb: DbHandle | null;
  temporaryDb: DbHandle | null;
  remoteOnly: boolean;
  initialSyncCompleted: boolean | undefined;
  /** Has this device ever finished a full download of this database, in any session?
   *  Filled from `hasSyncedOnThisDeviceMarker` — see `database-wrapper.ts`. #5068 uses
   *  it, together with `isUserDb`, to decide whether the window below opens. */
  hasSyncedOnThisDevice: boolean;
  /** Is this the user's OWN database, the one this device writes to? #5068 needs it
   *  because the two kinds of database fail in opposite directions: this device's copy
   *  of its own database can only be equal to or ahead of the server's, while its copy
   *  of a shared database it only ever DOWNLOADS can only be behind.
   *
   *  That second half holds for an ordinary account and not for an officer, whose account
   *  syncs the shared databases two-way. This rule is not what covers the officer — see
   *  `passThroughReadMayFindLocalAhead` below, which answers that case per document. */
  isUserDb: boolean;
}

/** Choose the DB instance for a read. Returns remoteDb during the pass-
 *  through window (initialSyncCompleted === undefined) so users can land
 *  in the app before sync completes; read wrappers graft results into
 *  local PouchDB so subsequent writes extend the same rev chain.
 *  temporaryDb (coach impersonation) short-circuits with no pass-through.
 *
 *  #5068: for the USER's own database the window closes once this device has ever
 *  finished a download here, because the device's copy of a document it wrote cannot
 *  be behind the server's. A shared database keeps the window in every case, because a
 *  device that only DOWNLOADS one can only be behind. Why that split, what it costs and
 *  what it leaves resting on the localStorage markers:
 *  design-docs/pass-through-db.md, "The user database stops passing through once the
 *  device has data of its own (#5068)".
 *
 *  An officer's device does NOT only download the shared databases, so that reasoning
 *  does not cover it. The routing here is deliberately unchanged for it: the answer is
 *  per document rather than per database, and lives in
 *  `passThroughReadMayFindLocalAhead` below. */
export function chooseReadDb(state: PassThroughDbState): DbHandle | null {
  if (state.temporaryDb) {
    return state.temporaryDb;
  }
  if (state.remoteOnly) {
    return state.remoteDb;
  }
  // read this device's own database rather than the server's copy of it, once this
  // device has data of its own to read
  const readsOwnDataLocally = state.isUserDb && state.hasSyncedOnThisDevice;
  if (state.initialSyncCompleted === undefined && state.remoteDb && !readsOwnDataLocally) {
    return state.remoteDb;
  }
  return state.localDb;
}

/** The value the read gate (`initialSyncCompleted`) takes when one round of the initial
 *  sync fails and the retry loop will try again (#6554).
 *
 *  Online, the gate stays open (`undefined`), so reads keep going to the server while the
 *  retry loop waits. The local store is still half downloaded at that point, so reading
 *  it would answer "does not exist" for every document not yet downloaded. Offline, the
 *  gate closes (`false`): the server cannot answer, and the local store is all there is.
 *  This is the same split the retry loop applies once every attempt is used up; see
 *  design-docs/pass-through-db.md, "Hybrid Failure Mode". */
export function readGateAfterFailedInitialSyncRound(isOnline: boolean): false | undefined {
  return isOnline ? undefined : false;
}

/** Choose the DB instance for a write. Writes go to temporaryDb when
 *  impersonating, to remoteDb when the wrapper is in remoteOnly mode
 *  (admin-impersonated downloads — there is no localDb), otherwise to
 *  localDb. Writes never pass through to remote during the normal
 *  initial-sync gate window; the sync pipeline pushes local writes in
 *  the background. */
export function chooseWriteDb(state: PassThroughDbState): DbHandle | null {
  if (state.temporaryDb) {
    return state.temporaryDb;
  }
  if (state.remoteOnly) {
    return state.remoteDb;
  }
  return state.localDb;
}

/** What `passThroughReadMayFindLocalAhead` needs to know about a read that has just
 *  happened. Every field is read fresh off the wrapper at the call site. */
export interface LocalAheadCheckState {
  /** did this read come from the pass-through remote, rather than from the local store? */
  readFromRemote: boolean;
  /** is there a local store to compare against at all? (a `remoteOnly` wrapper has none) */
  hasLocalDb: boolean;
  /** is this the user's OWN database? `chooseReadDb` already keeps that one local once
   *  the device has synced here, so a pass-through read of it needs no second check. */
  isUserDb: boolean;
  /** does this signed-in account WRITE the shared databases? An officer's account syncs
   *  them two-way (see `startSyncInternalAsync`), so an officer's device can be carrying
   *  a change to a curated document that it has not managed to push. An ordinary account
   *  only ever downloads them, so its copy can never be ahead of the server's. */
  deviceWritesSharedDbs: boolean;
}

/** Could a pass-through read have just fetched an OLDER copy than this device already holds?
 *
 *  `chooseReadDb` cannot answer this, because it is never told which document is being read —
 *  the same shape as #4798's device-local exception, which the read wrappers also have to ask
 *  about for themselves. So the read wrappers ask here, and when the answer is yes they check
 *  the local store before handing the server's body to the app.
 *
 *  It is false for everybody except an officer reading a shared database inside the
 *  pass-through window, so an ordinary account's boot does no extra work at all. */
export function passThroughReadMayFindLocalAhead(state: LocalAheadCheckState): boolean {
  return state.readFromRemote && state.hasLocalDb && !state.isUserDb && state.deviceWritesSharedDbs;
}

/** How this device's copy of a document compares with the copy a pass-through read
 *  just fetched from the server. */
export enum PassThroughLocalCopyRelation {
  /** this device holds no copy, the same revision, or an older one — the ordinary case,
   *  and the one where the server's body is the right answer */
  NotAhead = 'not-ahead',
  /** this device's copy is at a strictly higher revision generation, so it descends from
   *  the server's and carries something the server has not been told about yet */
  Ahead = 'ahead',
  /** same generation, different revision: the two copies have diverged and NEITHER is
   *  newer. Nothing here can say which is right, so the caller keeps using the server's */
  DivergedAtSameGeneration = 'diverged-at-same-generation',
}

/** Compare this device's revision of a document against the server's. Revisions look like
 *  `4-a1b2c3`; the number in front is the generation, which counts how many times the
 *  document has been written. */
export function compareLocalCopyToPassThroughBody(
  localRev: string | null | undefined,
  remoteRev: string | null | undefined,
): PassThroughLocalCopyRelation {
  if (!localRev || !remoteRev || localRev === remoteRev) return PassThroughLocalCopyRelation.NotAhead;
  const localGeneration = parseRevGeneration(localRev);
  const remoteGeneration = parseRevGeneration(remoteRev);
  if (localGeneration > remoteGeneration) return PassThroughLocalCopyRelation.Ahead;
  if (localGeneration === remoteGeneration) return PassThroughLocalCopyRelation.DivergedAtSameGeneration;
  return PassThroughLocalCopyRelation.NotAhead;
}

/** Drop every device-local document from a list that is about to leave this device.
 *
 *  A device-local document is one each device works out and keeps for itself, and it must
 *  never reach the server — see `design-docs/sync-integrity.md`. The exclusion lives in one
 *  list (`DEVICE_LOCAL_DOC_IDS` in `database-wrapper.ts`), and #4798's lesson is that the
 *  list only protects the paths that actually read it: every path that can move a document
 *  from this device to the server has to consult it itself. This is that consultation for the
 *  restore and the in-app snapshot — see the #4798 section of that doc for why neither is
 *  covered by any of the replication guards, and why a dropped document is not reported. */
export function withoutDeviceLocalDocs<T extends { _id?: string }>(
  docs: readonly T[],
  deviceLocalDocIds: readonly string[],
): T[] {
  return docs.filter(doc => !(doc != null && typeof doc._id === 'string' && deviceLocalDocIds.includes(doc._id)));
}

/** One row of `allDocs({ keys })` without bodies: a held id carries `value.rev`, a missing
 *  one carries `error`, and a deleted one has `value.deleted`. */
export interface LocalRevisionRow {
  key: string;
  error?: string;
  value?: { rev: string; deleted?: boolean };
}

/** The live revision the local store holds for each id; an id that is missing or deleted
 *  is left out, so it reads as "not held". */
function heldRevisionsById(rows: readonly LocalRevisionRow[] | undefined): Map<string, string> {
  const held = new Map<string, string>();
  for (const row of rows ?? []) {
    if (row && !row.error && row.value?.rev && !row.value.deleted) {
      held.set(row.key, row.value.rev);
    }
  }
  return held;
}

/** Read which revision the store holds for each id. Never throws: a failed read answers
 *  `held: null` with the error, so the caller still writes and then tells everybody.
 *  Tracked under its own name, so a store that stalls here is not reported as a stalled write. */
async function readHeldRevisionsAsync(
  localDb: NonNullable<PassThroughGraftDeps['localDb']>,
  databaseId: string,
  ids: string[],
): Promise<{ held: Map<string, string> | null; error: unknown }> {
  try {
    const answer = await trackLocalOpAsync(
      localOpName(databaseId, 'localDb.allDocs:passThroughGraftRevisionCheck'),
      () => localDb.allDocs({ keys: ids }),
    );
    return { held: heldRevisionsById(answer?.rows), error: null };
  } catch (err) {
    return { held: null, error: err };
  }
}

/** Dependencies graftPassThroughDocs needs, hoisted so the helper is a
 *  pure function we can unit test. */
export interface PassThroughGraftDeps {
  localDb: {
    bulkDocs: (docs: PouchDoc[], opts: { new_edits: boolean }) => Promise<unknown>;
    /** #6509: asked, before the graft, which revision the store already holds for each id */
    allDocs: (opts: { keys: string[] }) => Promise<{ rows: LocalRevisionRow[] }>;
  } | null;
  trackSilentProblem: (name: string, ctx: Record<string, unknown>) => void;
  /** `DatabaseRecoveryService.interceptDatabaseError`. A failed graft is offered here FIRST:
   *  true means the database recovery has taken the failure over (and reports it itself, or
   *  deliberately not at all), so the graft must not file a report of its own (df-335cc21f8427). */
  intercept: (err: unknown) => boolean;
  notifyDocId: (docId: string) => void;
  /** Which database this graft writes to, e.g. `user` or `global_common`.
   *  #4401 only: it names the tracked local operation, so a stalled-boot report
   *  can say WHICH store stopped answering. Several wrappers graft at once
   *  during the shared-database phase, and without this they are indistinguishable. */
  databaseId: string;
  /** Documents each device keeps to itself, which must never be written into the local
   *  store from a pass-through read (#4798). The read wrappers already avoid asking the
   *  remote for these; this is the backstop for the readers that fetch by key RANGE and
   *  so cannot name the ids they will get back. */
  deviceLocalDocIds: readonly string[];
}

/** Graft pass-through-read docs into local PouchDB so subsequent writes
 *  extend the remote revision tree instead of forking an orphan local
 *  tree. Uses bulkDocs({new_edits:false}) — same primitive PouchDB
 *  replication uses (see syncLogDateRangeAsync and the realtime-socket
 *  handler in database-wrapper.ts). Fires `notifyDocId` per doc whose
 *  revision the store did not already hold (#6509), because the wrapper's
 *  watcher firing is wired through the sync change path, not the local
 *  changes feed. Failure is offered to the database recovery
 *  first (`deps.intercept`), and logged via trackSilentProblem only when the
 *  recovery does not take it — caller still has the docs either way; the next
 *  read re-fetches and re-grafts.
 *
 *  The `db` parameter is the DB the caller actually read from; the
 *  helper no-ops when that's not the remote pass-through path, so call
 *  sites can pass `db` unconditionally without an `if (db === remoteDb)`
 *  guard.
 *
 *  IMPORTANT: callers must `registerDocWithWatcher` BEFORE calling
 *  this helper. The notify path inside DatabaseWrapper.notifyDocWatchers
 *  falls through to broadcast `notifyNew` to ALL active watchers when
 *  the docId is untracked — calling graft before registration would
 *  storm every watcher in the app. */
export async function graftPassThroughDocs(
  deps: PassThroughGraftDeps,
  db: DbHandle | null,
  remoteDb: DbHandle | null,
  docs: (PouchDoc | null | undefined)[],
  /** How long to wait for the local write before giving up on it (#4401). Injected only
   *  so a test can use a short one — production always takes the default. */
  timeoutMs: number = LOCAL_STORE_BOOT_OP_TIMEOUT_MS,
): Promise<void> {
  // graft only when the read was from the pass-through remote and the
  // local DB exists
  if (db !== remoteDb || !remoteDb || !deps.localDb) return;
  // filter to docs with an _id — allDocs({keys}) rows can have missing
  // .doc, view queries can emit keys that don't resolve to a doc, etc.
  // Doing the safety filter here means callers don't have to repeat it.
  //
  // #4798: and never graft a device-local document. Grafting one adopts the server's
  // revision history for a document this device is supposed to work out for itself, so
  // another device's copy becomes the local winner. The read wrappers keep these ids away
  // from the remote in the first place; this catches the readers that ask by key RANGE and
  // therefore cannot know in advance which ids come back.
  const safeDocs = docs.filter((doc): doc is PouchDoc =>
    doc != null && typeof doc._id === 'string' && !deps.deviceLocalDocIds.includes(doc._id));
  if (!safeDocs.length) return;
  const operationName = localOpName(deps.databaseId, 'localDb.bulkDocs:passThroughGraft');
  // #6509: the bound covers the revision check AND the write, so its name says both; each
  // step is tracked under its own name, which is what a stalled-boot report names
  const boundName = localOpName(deps.databaseId, 'localDb.allDocs+bulkDocs:passThroughGraft');
  const graftTimer = runningTimeTimer();
  const localDb = deps.localDb;
  try {
    // #4401: this write is BOUNDED, and the bound is what keeps the boot alive. The
    // catch below can only see a rejection, and a promise that never settles produces
    // none — so before the bound existed, a store that had stopped answering made the
    // boot hang here in complete silence. Production reports show exactly that: this
    // write queued behind an already-wedged read while the singleton batch above it,
    // which IS bounded, had long since returned.
    //
    // Abandoning this particular write is safe in a way that most writes are not: the
    // caller already holds the documents it read from the remote, so the boot carries
    // on with the right data and only loses the local copy — which the next read
    // re-fetches and re-grafts. That is the same argument the failure path below has
    // always rested on; the bound just extends it to a store that says nothing at all.
    //
    // #6509: the revision check runs inside the same bounded operation, so a store that
    // stops answering it is given up on exactly like one that stops answering the write.
    const checkThenWriteAsync = async () => {
      const check = await readHeldRevisionsAsync(localDb, deps.databaseId, safeDocs.map(doc => doc._id));
      await trackLocalOpAsync(operationName, () => localDb.bulkDocs(safeDocs, { new_edits: false }));
      return check;
    };
    const revisionCheck = await withLocalStoreTimeout(
      checkThenWriteAsync(),
      boundName,
      timeoutMs,
      // #6093: running time only, so a phone that froze the app mid-write does not abandon
      // it the moment it wakes
      graftTimer.schedule,
      graftTimer.clear,
    );
    if (revisionCheck.error != null && !deps.intercept(revisionCheck.error)) {
      deps.trackSilentProblem('pass-through graft revision check failed', {
        docCount: safeDocs.length,
        firstDocId: safeDocs[0]?._id,
        err: revisionCheck.error,
      });
    }
    // #6509: tell watchers only about revisions this device did not already hold. A re-read
    // of an unchanged document announced a change, and a watcher whose callback re-reads it
    // (the workout page's catalogue reads) then rebuilt itself every 1-2 seconds for ever.
    // When the check could not be read, everything is announced, as before: a broken check
    // must not cost a screen a real update.
    const held = revisionCheck.held;
    for (const doc of safeDocs) {
      if (held && held.get(doc._id) === doc._rev) continue;
      deps.notifyDocId(doc._id);
    }
  } catch (err) {
    // if the database recovery owns this failure (a broken IndexedDB, or a store that did
    // not answer), hand it over and report nothing: the recovery files its own report, and
    // for a genuine IndexedDB fault it deliberately files none. Reporting it here as well
    // turned one device fault into a second Sentry issue, and when no recovery was running
    // yet it never started one (df-335cc21f8427, as the pull catch-up did in #6230).
    if (deps.intercept(err)) return;
    // name the two apart: a graft that FAILED is an ordinary, recoverable miss, while
    // a store that never answered means every later local operation is queued behind
    // it — a much bigger fact, and the one this whole ticket is about
    deps.trackSilentProblem(
      isLocalStoreNotAnsweringError(err) ? 'pass-through graft abandoned — local store did not answer' : 'pass-through graft failed',
      {
        docCount: safeDocs.length,
        firstDocId: safeDocs[0]?._id,
        err,
      });
  }
}
